import React, { useState, useEffect, useCallback, useMemo, Component, ErrorInfo, ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Search, Loader2, AlertCircle, Package, ChevronLeft, ChevronRight,
  Star, ShoppingCart, AlertTriangle, Archive
} from 'lucide-react';
import { Product, FairVendorProduct, Fair } from '../types';
import { vendorApi, productCategoryApi, fairApi } from '../services/api';
import { useVendor } from '../contexts/VendorContext';
import { extractApiError } from '../utils/errorUtils';
import { formatCurrency } from '../utils/currency';
import { formatDateOnly } from '../utils/fairStatus'; // Issue #1672
import { useDebounce } from '../hooks/useDebounce';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import Decimal from 'decimal.js';
import ErrorAlert from './ErrorAlert';
import AddToFairModal from './VendorProductCatalog/AddToFairModal';
import EditFairProductModal from './VendorProductCatalog/EditFairProductModal';

type ViewMode = 'master' | 'fair';
type SortField = 'name' | 'price' | 'inventory' | 'featured';
type SortOrder = 'asc' | 'desc';

// Constants
const PAGINATION_PAGE_SIZE = 20;
const LOW_STOCK_THRESHOLD = 5;
const OUT_OF_STOCK_THRESHOLD = 0;

// Error Boundary for graceful error handling
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ProductCatalogErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Product Catalog Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Something Went Wrong</h3>
            <p className="text-gray-600 mb-4">
              There was an error loading the product catalog. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function VendorProductCatalog() {
  // Authorization: VendorAuthGuard ensures user is vendor/admin.
  // VendorContext only returns vendors owned by current user (via /user/vendors API).
  // Component only allows managing products for vendors in context, ensuring proper authorization.
  const { vendors, loadVendors } = useVendor();
  const { vendorId: routeVendorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [fairProducts, setFairProducts] = useState<FairVendorProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // Debounce search for performance
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<{ id: number; name: string; icon?: string }[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(
    routeVendorId ? Number(routeVendorId) : null
  );
  const [viewMode, setViewMode] = useState<ViewMode>('master');
  const [selectedFairId, setSelectedFairId] = useState<number | null>(null);
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Modals
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddToFairModal, setShowAddToFairModal] = useState<Product | null>(null);
  const [showEditFairProductModal, setShowEditFairProductModal] = useState<FairVendorProduct | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Reload trigger for manual refreshes
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Feature flag for pre-orders (Issue #1468)
  const { isEnabled: preOrdersEnabled } = useFeatureFlag('preorders_enabled');
  const triggerReload = useCallback(() => setReloadTrigger(prev => prev + 1), []);

  // Load vendors on mount
  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Select first vendor by default
  useEffect(() => {
    if (vendors.length > 0 && !selectedVendorId) {
      setSelectedVendorId(vendors[0].id);
    }
  }, [vendors, selectedVendorId]);

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await productCategoryApi.getCategories();
        setCategories(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load categories:', apiError);
      }
    };
    fetchCategories();
  }, []);

  // Load fairs for vendor
  useEffect(() => {
    const loadFairs = async () => {
      if (!selectedVendorId) return;
      try {
        const reservations = await vendorApi.getVendorReservations(selectedVendorId);
        const fairIds = [...new Set(reservations.map(r => r.fair_id))];
        const fairPromises = fairIds.map(id => fairApi.getById(id));
        const fairsData = await Promise.all(fairPromises);
        setFairs(fairsData.filter((f): f is Fair => f !== null));
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load fairs:', apiError);
      }
    };
    loadFairs();
  }, [selectedVendorId]);

  // Load products when dependencies change - with proper race condition prevention
  useEffect(() => {
    if (!selectedVendorId) return;
    if (viewMode === 'fair' && !selectedFairId) return;

    // Create abort controller for this request
    const abortController = new AbortController();
    let isMounted = true;

    const loadProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        if (viewMode === 'master') {
          const response = await vendorApi.getVendorProducts(selectedVendorId, currentPage, PAGINATION_PAGE_SIZE);
          // Only update state if component is still mounted and request wasn't aborted
          if (isMounted && !abortController.signal.aborted) {
            setProducts(response.items);
            setTotalPages(response.total_pages);
            setTotalItems(response.total);
          }
        } else if (viewMode === 'fair' && selectedFairId) {
          const response = await vendorApi.getVendorProductsForFair(
            selectedVendorId,
            selectedFairId,
            currentPage,
            PAGINATION_PAGE_SIZE
          );
          // Only update state if component is still mounted and request wasn't aborted
          if (isMounted && !abortController.signal.aborted) {
            setFairProducts(response.items);
            setTotalPages(response.total_pages);
            setTotalItems(response.total);
          }
        }
      } catch (err: unknown) {
        // Don't show errors if request was aborted or component unmounted
        if (isMounted && !abortController.signal.aborted) {
          const apiError = extractApiError(err);
          console.error('Failed to load products:', apiError);
          setError(apiError.message);
        }
      } finally {
        if (isMounted && !abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    // Cleanup function to abort on unmount or deps change
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [selectedVendorId, viewMode, selectedFairId, currentPage, reloadTrigger]);

  // Update URL when fair is selected
  useEffect(() => {
    if (selectedFairId) {
      setSearchParams({ fairId: selectedFairId.toString() });
    } else {
      setSearchParams({});
    }
  }, [selectedFairId, setSearchParams]);

  // Read fair ID from URL on mount
  useEffect(() => {
    const fairIdParam = searchParams.get('fairId');
    if (fairIdParam) {
      const fairId = Number(fairIdParam);
      setSelectedFairId(fairId);
      setViewMode('fair');
    }
  }, [searchParams]);

  const handleDelete = async (productId: number) => {
    if (!selectedVendorId) return;

    try {
      setIsDeleting(true);
      await vendorApi.deleteVendorProduct(selectedVendorId, productId);
      setDeleteConfirm(null);
      triggerReload();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to delete product:', apiError);
      setError(apiError.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveFromFair = async (productId: number) => {
    if (!selectedVendorId || !selectedFairId) return;

    try {
      setIsDeleting(true);
      await vendorApi.removeProductFromFairCatalog(selectedVendorId, selectedFairId, productId);
      setDeleteConfirm(null);
      triggerReload();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to remove from fair:', apiError);
      setError(apiError.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  // Filter and sort products with memoization for performance
  // NOTE: Known limitation - client-side filtering on server-paginated data.
  // This means pagination totals show all products, but filters only apply to current page.
  // Future improvement: Move filtering to server-side API or fetch all products client-side.
  // For now, this provides acceptable UX for catalogs <1000 products with 20/page.
  const filteredProducts = useMemo(() => {
    let items = viewMode === 'master' ? products : fairProducts.map(fp => fp.product!);

    // Filter by search and category (using debounced search term)
    items = items.filter(product => {
      const matchesSearch = debouncedSearchTerm === '' ||
        product.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (product.variety && product.variety.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
      const matchesCategory = selectedCategory === '' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    // Sort
    items.sort((a, b) => {
      let compareValue = 0;

      switch (sortField) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'price': {
          const priceA = new Decimal(a.price || 0);
          const priceB = new Decimal(b.price || 0);
          compareValue = priceA.comparedTo(priceB);
          break;
        }
        case 'inventory': {
          if (viewMode === 'fair') {
            const fpA = fairProducts.find(fp => fp.product_id === a.id);
            const fpB = fairProducts.find(fp => fp.product_id === b.id);
            compareValue = (fpA?.quantity_available || 0) - (fpB?.quantity_available || 0);
          }
          break;
        }
        case 'featured': {
          if (viewMode === 'fair') {
            const fpA = fairProducts.find(fp => fp.product_id === a.id);
            const fpB = fairProducts.find(fp => fp.product_id === b.id);
            compareValue = (fpA?.is_featured ? 1 : 0) - (fpB?.is_featured ? 1 : 0);
          }
          break;
        }
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return items;
  }, [viewMode, products, fairProducts, debouncedSearchTerm, selectedCategory, sortField, sortOrder]);

  // Get low stock threshold - standardized null/undefined handling
  const isLowStock = (quantity: number | null | undefined): boolean => {
    return quantity != null && quantity > OUT_OF_STOCK_THRESHOLD && quantity <= LOW_STOCK_THRESHOLD;
  };

  const isOutOfStock = (quantity: number | null | undefined): boolean => {
    return quantity === OUT_OF_STOCK_THRESHOLD;
  };

  // Bulk actions with progress tracking and error recovery
  const handleBulkAddToFair = async () => {
    if (!selectedVendorId || !selectedFairId || selectedProducts.size === 0) return;

    const productIds = Array.from(selectedProducts);
    let succeeded = 0;
    let failed = 0;
    const errors: string[] = [];

    try {
      setLoading(true);
      setError(null);

      // Process sequentially with progress tracking
      for (const productId of productIds) {
        try {
          await vendorApi.addProductToFairCatalog(selectedVendorId, selectedFairId, {
            product_id: productId,
            quantity_available: 0,
            is_featured: false,
            is_available_for_preorder: false
          });
          succeeded++;
        } catch (err: unknown) {
          failed++;
          const apiError = extractApiError(err);
          errors.push(`Product ${productId}: ${apiError.message}`);
          console.error(`Failed to add product ${productId} to fair:`, apiError);
        }
      }

      // Clear selection and reload
      setSelectedProducts(new Set());
      triggerReload();

      // Show summary of results
      if (failed > 0) {
        setError(`Added ${succeeded} product(s) successfully. ${failed} failed: ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Bulk operation failed:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedVendor && vendors.length > 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (vendors.length === 0 && !loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vendor Profile</h3>
          <p className="text-gray-600 mb-4">
            You need to create a vendor profile before managing products.
          </p>
          <Link
            to="/vendor/profile/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-4 h-4" />
            Create Vendor Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-6 h-6" />
              Product Catalog Manager
            </h1>
            {selectedVendor && (
              <p className="text-gray-600 mt-1">
                Managing products for <span className="font-semibold">{selectedVendor.business_name}</span>
              </p>
            )}
          </div>
          <Link
            to={`/vendor/products/new?vendorId=${selectedVendorId}`}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </Link>
        </div>

        {/* Vendor Selector (if multiple vendors) */}
        {vendors.length > 1 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Vendor Profile
            </label>
            <select
              value={selectedVendorId || ''}
              onChange={(e) => {
                setSelectedVendorId(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.business_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* View Mode Tabs */}
        <div role="tablist" aria-label="Product view modes" className="flex items-center gap-4 mb-4 border-b border-gray-200">
          <button
            role="tab"
            aria-selected={viewMode === 'master'}
            aria-controls="product-content"
            onClick={() => {
              setViewMode('master');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              viewMode === 'master'
                ? 'border-primary text-secondary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Master Catalog
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'fair'}
            aria-controls="product-content"
            onClick={() => {
              setViewMode('fair');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              viewMode === 'fair'
                ? 'border-primary text-secondary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Fair-Specific Products
          </button>
        </div>

        {/* Fair Selector */}
        {viewMode === 'fair' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Fair
            </label>
            <select
              value={selectedFairId || ''}
              onChange={(e) => {
                setSelectedFairId(Number(e.target.value) || null);
                setCurrentPage(1);
              }}
              className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select a fair...</option>
              {fairs.map(fair => (
                <option key={fair.id} value={fair.id}>
                  {fair.name} - {formatDateOnly(fair.start_date)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Search, Filters, and Sort */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search products by name, variety, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                aria-label="Search products"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              aria-label="Filter by category"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-4">
            <label htmlFor="sort-field-select" className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              id="sort-field-select"
              value={sortField}
              onChange={(e) => setSortField(e.target.value as SortField)}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              aria-label="Sort field"
            >
              <option value="name">Name</option>
              <option value="price">Price</option>
              {viewMode === 'fair' && <option value="inventory">Inventory</option>}
              {viewMode === 'fair' && <option value="featured">Featured</option>}
            </select>
            <button
              onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              aria-label={`Sort order: ${sortOrder === 'asc' ? 'ascending' : 'descending'}. Click to toggle.`}
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>
          </div>

          {/* Bulk Actions */}
          {viewMode === 'master' && selectedProducts.size > 0 && selectedFairId && (
            <div className="bg-moonbone border border-teal-200 rounded-lg p-4 flex items-center justify-between">
              <span className="text-sm text-ink">
                {selectedProducts.size} product(s) selected
              </span>
              <button
                onClick={handleBulkAddToFair}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
              >
                Add to Fair
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <ErrorAlert
          message={error}
          onDismiss={() => setError(null)}
          className="mb-6"
        />
      )}

      {/* Loading State */}
      {loading && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center py-12"
        >
          <Loader2 className="w-8 h-8 animate-spin text-secondary" aria-hidden="true" />
          <span className="sr-only">Loading products...</span>
        </div>
      )}

      {/* Products Display */}
      {!loading && viewMode === 'fair' && !selectedFairId && (
        <div className="bg-moonbone border border-teal-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Fair</h3>
          <p className="text-gray-600">
            Please select a fair from the dropdown above to manage fair-specific products.
          </p>
        </div>
      )}

      {!loading && (viewMode === 'master' || (viewMode === 'fair' && selectedFairId)) && (
        <div id="product-content" role="tabpanel" aria-label={`${viewMode === 'master' ? 'Master catalog' : 'Fair-specific'} products`}>
          {filteredProducts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedCategory
                  ? 'Try adjusting your search or filters.'
                  : viewMode === 'fair'
                  ? 'No products have been added to this fair yet. Switch to Master Catalog to add products.'
                  : 'Get started by adding your first product.'}
              </p>
              {viewMode === 'master' && (
                <Link
                  to={`/vendor/products/new?vendorId=${selectedVendorId}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Product
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Product Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map(product => {
                  const fairProduct = viewMode === 'fair'
                    ? fairProducts.find(fp => fp.product_id === product.id)
                    : undefined;
                  const isSelected = selectedProducts.has(product.id);

                  return (
                    <div
                      key={product.id}
                      className={`bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4 border-2 ${
                        isSelected ? 'border-dusty-teal' : 'border-transparent'
                      }`}
                    >
                      {/* Selection Checkbox (Master View) */}
                      {viewMode === 'master' && selectedFairId && (
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSelected = new Set(selectedProducts);
                              if (e.target.checked) {
                                newSelected.add(product.id);
                              } else {
                                newSelected.delete(product.id);
                              }
                              setSelectedProducts(newSelected);
                            }}
                            className="w-4 h-4 text-secondary rounded focus:ring-2 focus:ring-primary"
                          />
                          <label className="ml-2 text-sm text-gray-600">Select for bulk add</label>
                        </div>
                      )}

                      {/* Product Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {product.emoji && (
                            <span className="text-3xl flex-shrink-0">{product.emoji}</span>
                          )}
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 line-clamp-2">
                              {product.name}
                            </h3>
                            {product.variety && (
                              <p className="text-sm text-gray-500 truncate">
                                {product.variety}
                              </p>
                            )}
                          </div>
                        </div>
                        {fairProduct?.is_featured && (
                          <Star className="w-5 h-5 text-yellow-500 fill-current flex-shrink-0" />
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="space-y-2 mb-4">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Category:</span> {product.category}
                        </div>
                        {product.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {product.description}
                          </p>
                        )}
                        {viewMode === 'master' && product.price && (
                          <div className="text-sm text-gray-900">
                            <span className="font-medium">Base Price:</span> {formatCurrency(product.price)}
                          </div>
                        )}
                      </div>

                      {/* Fair-Specific Details */}
                      {viewMode === 'fair' && fairProduct && (
                        <div className="space-y-2 mb-4 p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Fair Price:</span>{' '}
                            <span className="text-gray-900">
                              {fairProduct.fair_specific_price
                                ? formatCurrency(fairProduct.fair_specific_price)
                                : formatCurrency(product.price || '0')}
                            </span>
                            {fairProduct.fair_specific_price && (
                              <span className="ml-1 text-xs text-gray-500">
                                (overrides base)
                              </span>
                            )}
                          </div>
                          <div className="text-sm">
                            <span className="font-medium text-gray-700">Inventory:</span>{' '}
                            <span className={`font-semibold ${
                              isOutOfStock(fairProduct.quantity_available)
                                ? 'text-red-600'
                                : isLowStock(fairProduct.quantity_available)
                                ? 'text-yellow-600'
                                : 'text-green-600'
                            }`}>
                              {fairProduct.quantity_available ?? 0} units
                            </span>
                            {isLowStock(fairProduct.quantity_available) && (
                              <AlertTriangle className="inline-block w-4 h-4 ml-1 text-yellow-600" />
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {fairProduct.is_featured && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                <Star className="w-3 h-3" />
                                Featured
                              </span>
                            )}
                            {preOrdersEnabled && fairProduct.is_available_for_preorder && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-moonbone text-ink rounded-full text-xs">
                                <ShoppingCart className="w-3 h-3" />
                                Pre-order
                              </span>
                            )}
                            {isOutOfStock(fairProduct.quantity_available) && !(preOrdersEnabled && fairProduct.is_available_for_preorder) && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                <Archive className="w-3 h-3" />
                                Out of Stock
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {viewMode === 'master' && (
                          <>
                            <Link
                              to={`/vendor/products/${product.id}/edit?vendorId=${selectedVendorId}`}
                              className="flex-1 px-3 py-2 text-sm text-secondary bg-moonbone hover:bg-moonbone rounded-lg flex items-center justify-center gap-1"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </Link>
                            {selectedFairId && (
                              <button
                                onClick={() => setShowAddToFairModal(product)}
                                className="flex-1 px-3 py-2 text-sm text-secondary bg-teal-50 hover:bg-secondary-light/100 rounded-lg flex items-center justify-center gap-1"
                              >
                                <Plus className="w-4 h-4" />
                                Add to Fair
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteConfirm(product.id)}
                              className="px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {viewMode === 'fair' && fairProduct && (
                          <>
                            <button
                              onClick={() => setShowEditFairProductModal(fairProduct)}
                              className="flex-1 px-3 py-2 text-sm text-secondary bg-moonbone hover:bg-moonbone rounded-lg flex items-center justify-center gap-1"
                            >
                              <Edit className="w-4 h-4" />
                              Edit Fair Details
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(product.id)}
                              className="px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"
                              title="Remove from Fair"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * PAGINATION_PAGE_SIZE) + 1} to {Math.min(currentPage * PAGINATION_PAGE_SIZE, totalItems)} of {totalItems} products
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add to Fair Modal */}
      {showAddToFairModal && selectedFairId && selectedVendorId && (
        <AddToFairModal
          product={showAddToFairModal}
          fairId={selectedFairId}
          vendorId={selectedVendorId}
          onClose={() => setShowAddToFairModal(null)}
          onSuccess={() => {
            setShowAddToFairModal(null);
            triggerReload();
          }}
        />
      )}

      {/* Edit Fair Product Modal */}
      {showEditFairProductModal && selectedVendorId && selectedFairId && (
        <EditFairProductModal
          fairProduct={showEditFairProductModal}
          vendorId={selectedVendorId}
          fairId={selectedFairId}
          onClose={() => setShowEditFairProductModal(null)}
          onSuccess={() => {
            setShowEditFairProductModal(null);
            triggerReload();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="presentation"
          tabIndex={-1}
          onClick={() => !isDeleting && setDeleteConfirm(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isDeleting) {
              setDeleteConfirm(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="delete-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
              Confirm {viewMode === 'fair' ? 'Remove' : 'Delete'}
            </h3>
            <p className="text-gray-600 mb-6">
              {viewMode === 'fair'
                ? 'Are you sure you want to remove this product from the fair? This will not delete the product from your master catalog.'
                : 'Are you sure you want to delete this product? This will remove it from all fairs and your master catalog. This action cannot be undone.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (viewMode === 'fair') {
                    handleRemoveFromFair(deleteConfirm);
                  } else {
                    handleDelete(deleteConfirm);
                  }
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {viewMode === 'fair' ? 'Removing...' : 'Deleting...'}
                  </>
                ) : (
                  viewMode === 'fair' ? 'Remove' : 'Delete'
                )}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with Error Boundary for production resilience
export default function VendorProductCatalogWithErrorBoundary() {
  return (
    <ProductCatalogErrorBoundary>
      <VendorProductCatalog />
    </ProductCatalogErrorBoundary>
  );
}
